/* eslint-disable max-statements */
import {useState, useCallback, memo, useRef, useEffect} from 'react';
import * as store from '@/store';
import {ICurDocCardLikesInfo, IDocCardLikeInfo} from '@/interface';
import {Popper, PopperTrigger} from '@baidu/luffy-comp-popper';
import personalLikeSvg from '@/assets/svg/personalLike.svg?tsx';
import Icon, {IconSize} from '@/components/Icon';
import gainLikeSvg from '@/assets/svg/gainLike.svg?tsx';
import styles from './index.module.scss';
import Avatar, {AvatarSize} from '@/components/Avatar';
import sdk from '@/sdk';
import {STATUS} from '@/Constant';
import {getAttrs} from '@/utils/arrGetAttrUtil';
import InfiniteScroll from 'react-infinite-scroller';
import classnames from 'classnames';
import Loading from '@/components/Loading';
import loadingImg from '@/assets/img/loading.png';
import PlaceholderIcon, {PlaceholderType} from '@/components/PlaceholderIcon';
import FailSvg from '@/assets/svg/fail.svg?tsx';
import classNames from 'classnames';
import {updateCardLikeInfo} from '@/store/util';
import NewGuid from './NewGuid';
import useCardLikeNewGuid from '@/hooks/document/useCardLikeNewGuid';
import routers, {buildPath} from '@/config/routers';
import {openUrlOnNewWindow} from '@/utils/browserUtils';
import {
    cardLikeChangeLog,
    hoverCardLikeIconLog,
    hoverCardLikeListUserItemLog,
    showCardLikeListLog,
} from '@/buried/docCard';
import {isUndefined} from 'lodash-es';
import toast, {ToastType} from '@/components/Toast';


interface ICardLikeComp {
    index: number;
    blockId: string;
    likeCount?: number;
    userLike?: boolean;
    isShowCardLikeNewGuid?: boolean;
    initTime: number;
}
interface User {
    userGuid: string;
    userName: string;
    avatarUrl: string;
}

const PAGE_SIZE = 20;
function CardLikeComp({
    index,
    blockId,
    likeCount = 0,
    userLike = false,
    isShowCardLikeNewGuid,
    initTime,
}: ICardLikeComp) {
    const [userLikeList, setUserLikeList] = useState<User[]>([]);
    const [status, setStatus] = useState('');
    const [pageNum, setPageNum] = useState(1);
    const likeLoading = useRef<boolean>(false);
    const {repositoryGuid, docGuid} = store.getCurDocument() || {};
    const scrollRef = useRef<HTMLDivElement>(null);
    const {showNewGuid, onCloseNewGuid} = useCardLikeNewGuid({
        isShow: isShowCardLikeNewGuid as boolean,
        index,
    });
    const [showLikeList, setShowLikeList] = useState(false);
    const likeWrapRef = useRef<HTMLDivElement>(null);
    const createdTime = useRef<number>(initTime);
    const [cardUserLike, setCardUserLike] = useState(userLike);
    const portalContainer = useRef(document.querySelector('.mp-editor-container-outer'));

    const getCurCardUserLikeInfo = useCallback(async (pageNumber?: number, userLike?: boolean) => {
        setStatus(STATUS.loading);
        if (!repositoryGuid || !docGuid) {
            return;
        }
        try {
            const params = {
                pageSize: PAGE_SIZE,
                pageNum: pageNumber || pageNum,
                blockId,
                repositoryGuid,
                docGuid,
                created: createdTime.current,
            };
            const ksServer = await sdk.getKsServer();
            const {count, likeList} = await ksServer.getCurCardLikeUserInfo(params);
            // 拿到所有的Guid去批量请求完整的点赞列表的数据
            const userGuidList = getAttrs(likeList, 'userGuid');
            const usersList = await sdk.as.getUserInfoList(userGuidList, false);
            const sortUsersList = (usersList: any[]) => {
                return usersList.sort((a, b) => {
                    return userGuidList.indexOf(a.userGuid) - userGuidList.indexOf(b.userGuid);
                });
            };
            const result = sortUsersList(usersList);
            if (params.pageNum === 1) {
                setUserLikeList(result);
                setStatus(usersList.length >= params.pageSize ? STATUS.success : STATUS.noData);
            } else {
                setUserLikeList([...userLikeList, ...result]);
                setStatus(usersList.length >= params.pageSize ? STATUS.success : STATUS.noScrollData);
            }

            if (!isUndefined(userLike)) {
                updateCardLikeInfo(docGuid, [{
                    blockId,
                    userLike: userLike,
                    likeCount: count,
                }]);
                // 取消点赞操作，更新最新的点赞列表，如果最新的点赞列表为空，则自动关闭列表浮层
                if (!userLike) {
                    if (count === 0) {
                        setShowLikeList(false);
                    }
                }
                cardLikeChangeLog({clickType: Number(userLike), number: count});
            }
            setPageNum(params.pageNum + 1);
        } catch (err) {
            setStatus(pageNum === 1 ? STATUS.error : STATUS.scrollError);
            if (!isUndefined(userLike)) {
                const count = userLike ? likeCount + 1 : likeCount - 1;
                updateCardLikeInfo(docGuid, [{
                    blockId,
                    userLike: userLike,
                    likeCount: count,
                }]);
                // 取消点赞操作，更新最新的点赞列表，如果最新的点赞列表获取失败，则前端根据缓存列表数据自动关闭列表浮层
                if (!userLike) {
                    if (count === 0) {
                        setShowLikeList(false);
                    }
                }
                cardLikeChangeLog({clickType: Number(userLike), number: count});
            }
            console.log(err);

        }

    }, [blockId, docGuid, likeCount, pageNum, repositoryGuid, userLikeList]);

    function handleChange(visible: boolean) {
        if (likeCount > 0) {
            setShowLikeList(visible);
            showCardLikeListLog();
            if ((!status || [STATUS.error, STATUS.scrollError].includes(status)) && visible) {
                getCurCardUserLikeInfo(1);
            }
        }
    }

    function loadMore() {
        if ([STATUS.loading, STATUS.noData, STATUS.noScrollData, STATUS.scrollError, STATUS.error].includes(status)) {
            return;
        }
        getCurCardUserLikeInfo();
    }

    function handleRetry() {
        if (status === STATUS.loading) {
            return;
        }
        getCurCardUserLikeInfo();
    }

    function reloadLikeList(userLike: boolean) {
        setUserLikeList([]);
        setStatus('');
        setPageNum(1);
        getCurCardUserLikeInfo(1, userLike);
    }
    async function cancelLike() {
        try {
            if (!repositoryGuid || !docGuid) {
                return;
            }
            const ksServer = await sdk.getKsServer();
            const {time = Date.now()} = await ksServer.cancelDocCardLike({
                repositoryGuid,
                docGuid,
                blockId,
            });
            setCardUserLike(false);
            createdTime.current = time;
            reloadLikeList(false);
        } catch (err) {
            console.log(err);
            toast({
                content: '取消点赞失败，请稍后再试',
                type: ToastType.Error,
            });
        } finally {
            likeLoading.current = false;
        }
    }

    async function giveLike() {
        try {
            if (!repositoryGuid || !docGuid) {
                return;
            }
            const ksServer = await sdk.getKsServer();
            const {time = Date.now()} = await ksServer.giveDocCardLike({
                repositoryGuid,
                docGuid,
                blockId,
            });
            setCardUserLike(true);
            createdTime.current = time;
            reloadLikeList(true);
        } catch (err) {
            console.log(err);
            toast({
                content: '点赞失败，请稍后再试',
                type: ToastType.Error,
            });
        } finally {
            likeLoading.current = false;
        }
    }

    function handleLikeChange() {
        if (showNewGuid) {
            onCloseNewGuid();
        }
        if (likeLoading.current) {
            return;
        }
        likeLoading.current = true;
        if (cardUserLike) {
            // 取消点赞
            cancelLike();
        } else {
            // 点赞
            giveLike();
        }
    }


    function getTarget() {
        return likeWrapRef.current;
    }

    function handleMouseEnter() {
        hoverCardLikeIconLog();
        if (showNewGuid) {
            onCloseNewGuid();
        }
    }

    function goPersonHome(avatar: string) {
        return () => {
            const url = buildPath(routers.PersonalHome.path, {
                userName: avatar,
            });
            openUrlOnNewWindow(url);
        };
    }

    function handlelistItemMouseEnter() {
        hoverCardLikeListUserItemLog();
    }

    if (!blockId) {
        return <></>;
    }

    return <>
        <PopperTrigger
            type="hover"
            mouseEnterDelay={100}
            mouseLeaveDelay={100}
            visible={showLikeList}
            onChange={handleChange}
        >
            <div className={styles.like}>
                <div
                    ref={likeWrapRef}
                    className={styles.likeWrap}
                    onClick={handleLikeChange}
                    onMouseEnter={handleMouseEnter}
                >
                    <Icon
                        className={styles.likeIcon}
                        svg={cardUserLike ? gainLikeSvg : personalLikeSvg}
                        size={IconSize.LG}
                    />
                    <span>{likeCount > 0 ? likeCount : '为TA点赞'}</span>
                </div>
            </div>
            <Popper
                className={styles.popperWrap}
                withArrow
                destroyOnHide={false}
                placement="bottom"
                eventsEnabled
                portalContainer={portalContainer.current}
                modifiers={{
                    flip: {
                        behavior: ['bottom'],
                    },
                    preventOverflow: {
                        escapeWithReference: true,
                    },
                }}
            >
                <div
                    ref={scrollRef}
                    className={classNames(
                        styles.likeListWrap,
                        (pageNum === 1 && status === STATUS.loading
                    || status === STATUS.error)
                    && styles.fixdHeight
                    )}
                >
                    <InfiniteScroll
                        useWindow={false}
                        hasMore={![STATUS.noData, STATUS.noScrollData].includes(status)}
                        initialLoad={false}
                        loadMore={loadMore}
                        getScrollParent={() => scrollRef.current}
                    >
                        {
                            userLikeList.map((item) => {
                                const {userGuid, nickname, department, email, avatar} = item;
                                return <div
                                    key={userGuid}
                                    className={styles.userCardWrap}
                                    onClick={goPersonHome(avatar)}
                                    onMouseEnter={handlelistItemMouseEnter}
                                >
                                    <Avatar className={styles.avatar} userGuid={userGuid} size={AvatarSize.SM} />
                                    <div className={styles.info}>
                                        <div className={styles.userInfo}>
                                            <span>{nickname}</span>
                                            <span className={styles.department}>{department}</span>
                                        </div>
                                        <div className={styles.email}>{email}</div>
                                    </div>
                                </div>;
                            })
                        }
                    </InfiniteScroll>
                    {
                        status === STATUS.loading
                    && <Loading
                        className={classnames(
                            styles.loading,
                            pageNum === 1 && !userLikeList.length ?
                                styles.loadingFull : styles.loadingBottom
                        )}
                        visible={status === STATUS.loading}
                        spinner={<img src={loadingImg} alt="" />}
                        message="加载中..."
                    />
                    }
                    {
                        status === STATUS.error
                    && <PlaceholderIcon
                        hint="加载失败，"
                        onRetry={handleRetry}
                        svg={FailSvg}
                        type={PlaceholderType.Retry}
                    />
                    }
                </div>
            </Popper>
        </PopperTrigger>
        {
            showNewGuid
            && <NewGuid
                onCloseNewGuid={onCloseNewGuid}
                getTarget={getTarget}
            />
        }
    </>;
}

const MemoCardLikeComp = memo(CardLikeComp);

interface ICardLike {
    blockId: string;
    index: number;
    curDocCardLikesInfo: ICurDocCardLikesInfo;
    isShowCardLikeNewGuid: boolean;
}

export const CardLike = store.connect([
    store.KEYS.CUR_DOC_CARD_LIKES_INFO,
    store.KEYS.IS_SHOW_CARD_LIKE_NEWGUID,
])(({
    blockId,
    index,
    isShowCardLikeNewGuid,
    curDocCardLikesInfo,
}: ICardLike) => {
    const curCardLikeData = curDocCardLikesInfo?.likeInfoList?.find(item => item.blockId === blockId) || {};
    return <MemoCardLikeComp
        {...curCardLikeData}
        blockId={blockId}
        index={index}
        isShowCardLikeNewGuid={isShowCardLikeNewGuid}
        initTime={curDocCardLikesInfo?.initTime || Date.now()}
    />;
});

